import { FormEvent, KeyboardEvent, RefObject, useEffect, useRef, useState } from 'react';

interface CommandInputProps {
  prompt: string;
  value: string;
  disabled?: boolean;
  inputRef?: RefObject<HTMLInputElement | null>;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onHistoryNavigate: (direction: 'up' | 'down') => void;
  onTabComplete: () => void;
}

export function CommandInput({
  prompt,
  value,
  disabled = false,
  inputRef,
  onChange,
  onSubmit,
  onHistoryNavigate,
  onTabComplete,
}: CommandInputProps) {
  const localInputRef = useRef<HTMLInputElement>(null);
  const resolvedInputRef = inputRef ?? localInputRef;
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!disabled) {
      resolvedInputRef.current?.focus();
    }
  }, [disabled, resolvedInputRef]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (disabled) {
      return;
    }
    onSubmit();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      onHistoryNavigate('up');
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      onHistoryNavigate('down');
      return;
    }

    if (event.key === 'Tab') {
      event.preventDefault();
      onTabComplete();
    }
  };

  return (
    <form className="command-input-form" onSubmit={handleSubmit}>
      <label className="command-input-line" htmlFor="terminal-command-input">
        <span className="prompt">{prompt}</span>
        <input
          ref={resolvedInputRef}
          id="terminal-command-input"
          autoFocus
          autoCapitalize="off"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          className="command-input"
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyDown={handleKeyDown}
        />
        <span className={`terminal-cursor ${isFocused && !disabled ? 'active' : ''}`.trim()} />
      </label>
    </form>
  );
}
